import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { type MultiFactorEnrollmentStart } from "~/gql/graphqlBase.generated";
import { type MyMultiFactorStatusFragment } from "~/gql/queries/multiFactor.generated";
import { type MutationPayloadWithErrors, type MutationValidationResult } from "~/helpers/runMutation";
import useMutationRunner from "~/hooks/useMutationRunner";
import {
	useCompleteMyMultiFactorEnrollmentMutation,
	useDisableMyMultiFactorMutation,
	useRegenerateMyMultiFactorRecoveryCodesMutation,
	useStartMyMultiFactorEnrollmentMutation
} from "~/services/endpoints/apiEndpoints";

export interface MultiFactorStartResult {
	enrollment: MultiFactorEnrollmentStart | null;
	error: string | null;
}

export interface MultiFactorCompleteResult {
	error: string | null;
	ok: boolean;
	recoveryCodes: string[];
	status: MyMultiFactorStatusFragment | null;
}

export interface MultiFactorSimpleResult {
	error: string | null;
	ok: boolean;
}

export interface MultiFactorRecoveryCodesResult {
	error: string | null;
	recoveryCodes: string[] | null;
}

export interface UseMultiFactorMutationsReturn {
	completeEnrollment: (secretBase32Encoded: string, code: string) => Promise<MultiFactorCompleteResult>;
	disableMultiFactor: (code: string) => Promise<MultiFactorSimpleResult>;
	isCompleting: boolean;
	isDisabling: boolean;
	isRegenerating: boolean;
	isStarting: boolean;
	regenerateRecoveryCodes: () => Promise<MultiFactorRecoveryCodesResult>;
	resetRegenerateRecoveryCodes: () => void;
	startEnrollment: () => Promise<MultiFactorStartResult>;
}

const resolveOutcomeError = <TPayload extends MutationPayloadWithErrors, TEntity>(
	result: MutationValidationResult<TPayload, TEntity>,
	fallbackMessage: string
): string | null => (result.ok ? null : (result.errors[0]?.message ?? fallbackMessage));

const useMultiFactorMutations = (): UseMultiFactorMutationsReturn => {
	const { t } = useTranslation("common");
	const { runForValidation } = useMutationRunner();
	const [startMutation, { isLoading: isStarting }] = useStartMyMultiFactorEnrollmentMutation();
	const [completeMutation, { isLoading: isCompleting }] = useCompleteMyMultiFactorEnrollmentMutation();
	const [disableMutation, { isLoading: isDisabling }] = useDisableMyMultiFactorMutation();
	const [regenerateMutation, { isLoading: isRegenerating, reset: resetRegenerateRecoveryCodes }] =
		useRegenerateMyMultiFactorRecoveryCodesMutation();

	const startEnrollment = useCallback(async (): Promise<MultiFactorStartResult> => {
		const fallbackMessage = t("preferences.security.enroll.startError");
		const result = await runForValidation(
			startMutation(),
			(data) => data.startMyMultiFactorEnrollment,
			(payload) => payload.enrollment,
			fallbackMessage,
			{ silent: true }
		);
		return { enrollment: result.entity, error: resolveOutcomeError(result, fallbackMessage) };
	}, [runForValidation, startMutation, t]);

	const completeEnrollment = useCallback(
		async (secretBase32Encoded: string, code: string): Promise<MultiFactorCompleteResult> => {
			const fallbackMessage = t("preferences.security.enroll.genericError");
			const result = await runForValidation(
				completeMutation({ input: { code, secretBase32Encoded } }),
				(data) => data.completeMyMultiFactorEnrollment,
				(payload) => payload.status,
				fallbackMessage,
				{ silent: true }
			);
			return {
				error: resolveOutcomeError(result, fallbackMessage),
				ok: result.ok,
				recoveryCodes: result.payload?.recoveryCodes ?? [],
				status: result.entity
			};
		},
		[completeMutation, runForValidation, t]
	);

	const disableMultiFactor = useCallback(
		async (code: string): Promise<MultiFactorSimpleResult> => {
			const fallbackMessage = t("preferences.security.disable.genericError");
			const result = await runForValidation(
				disableMutation({ input: { code } }),
				(data) => data.disableMyMultiFactor,
				(payload) => payload.status,
				fallbackMessage,
				{ silent: true }
			);
			return { error: resolveOutcomeError(result, fallbackMessage), ok: result.ok };
		},
		[disableMutation, runForValidation, t]
	);

	const regenerateRecoveryCodes = useCallback(async (): Promise<MultiFactorRecoveryCodesResult> => {
		const fallbackMessage = t("preferences.security.recoveryCodes.genericError");
		const result = await runForValidation(
			regenerateMutation(),
			(data) => data.regenerateMyMultiFactorRecoveryCodes,
			(payload) => payload.recoveryCodes,
			fallbackMessage,
			{ silent: true }
		);
		return { error: resolveOutcomeError(result, fallbackMessage), recoveryCodes: result.entity };
	}, [regenerateMutation, runForValidation, t]);

	return {
		completeEnrollment,
		disableMultiFactor,
		isCompleting,
		isDisabling,
		isRegenerating,
		isStarting,
		regenerateRecoveryCodes,
		resetRegenerateRecoveryCodes,
		startEnrollment
	};
};

export default useMultiFactorMutations;
